import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle, XCircle, X } from 'lucide-react';
import { useApprovalRequestDetails } from '@api/hooks/useApprovalRequests';
import { useWorkflowStatusSSE } from '@api/hooks/useWorkflowStatus';
import type { ApprovalRequestResponse, DocumentWorkflowState } from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';
import {
  type TrackedDocumentWorkflow,
  useDocumentWorkflowStore,
} from '@store/documentWorkflowStore';

const prettifyStatus = (value: string | null | undefined) => {
  if (!value) {
    return 'processing';
  }

  return value.replace(/_/g, ' ');
};

const formatDocumentType = (value: string) => {
  if (value === 'pending_classification') {
    return 'Document';
  }

  return prettifyStatus(value).replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeStatus = (value: string | null | undefined) => value?.toLowerCase() ?? '';

const isFailureStatus = (value: string | null | undefined) => {
  const normalized = normalizeStatus(value);
  return ['error', 'failed', 'rejected', 'cancelled', 'timed_out', 'expired'].some((status) =>
    normalized.includes(status)
  );
};

const toWorkflowState = (value: unknown): DocumentWorkflowState | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return value as DocumentWorkflowState;
};

const statusFromObject = (value: DocumentWorkflowState | null) => {
  const candidate = [
    value?.status,
    value?.stage,
    value?.extraction_status,
    value?.correlation_status,
  ].find((item) => typeof item === 'string' && item);

  return typeof candidate === 'string' ? candidate : null;
};

const isApprovalPending = (approvalRequest: ApprovalRequestResponse | null) =>
  approvalRequest?.status === 'pending' || approvalRequest?.status === 'escalated';

const hasSpawnedApPosting = (workflowState: DocumentWorkflowState | null, stage: string | null) =>
  Boolean(workflowState?.ap_posting_workflow_id)
  && ['ap_posting_started', 'correlating_document', 'waiting_for_corrections', 'completed'].includes(
    stage ?? ''
  );

const formatApprovalProgress = (approvalRequest: ApprovalRequestResponse) => {
  if (approvalRequest.total_steps && approvalRequest.current_step) {
    return `Approval step ${approvalRequest.current_step} of ${approvalRequest.total_steps} in progress`;
  }

  if (approvalRequest.current_step) {
    return `Approval step ${approvalRequest.current_step} in progress`;
  }

  return 'Approval in progress';
};

const buildStatusCopy = ({
  approvalRequest,
  workflowState,
  effectiveStatus,
  effectiveStage,
}: {
  approvalRequest: ApprovalRequestResponse | null;
  workflowState: DocumentWorkflowState | null;
  effectiveStatus: string | null;
  effectiveStage: string | null;
}) => {
  const workflowMessage = workflowState?.message?.trim() || null;

  if (approvalRequest?.status === 'rejected') {
    return {
      primary: 'Approval rejected',
      secondary: workflowMessage || null,
    };
  }

  if (isApprovalPending(approvalRequest)) {
    return {
      primary: formatApprovalProgress(approvalRequest!),
      secondary: workflowMessage || 'Waiting for approvers to review this document.',
    };
  }

  if (approvalRequest?.status === 'approved') {
    return {
      primary: hasSpawnedApPosting(workflowState, effectiveStage)
        ? 'Approval complete. AP posting started.'
        : 'Approval complete. Preparing the next step.',
      secondary: workflowMessage,
    };
  }

  if (workflowMessage) {
    const secondary =
      effectiveStage && prettifyStatus(effectiveStage) !== workflowMessage
        ? prettifyStatus(effectiveStage)
        : null;

    return {
      primary: workflowMessage,
      secondary,
    };
  }

  return {
    primary: prettifyStatus(effectiveStage || effectiveStatus),
    secondary: null,
  };
};

const DocumentWorkflowNotification = ({ workflow }: { workflow: TrackedDocumentWorkflow }) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  const removeWorkflow = useDocumentWorkflowStore((state) => state.removeWorkflow);
  const [terminalHandled, setTerminalHandled] = useState(false);

  const { status: workflowEvent, isConnected, isConnecting, error } = useWorkflowStatusSSE(
    workflow.workflowId
  );

  const liveWorkflowState = useMemo(
    () => toWorkflowState(workflowEvent?.status),
    [workflowEvent?.status]
  );

  const resultWorkflowState = useMemo(
    () => toWorkflowState(workflowEvent?.result),
    [workflowEvent?.result]
  );

  const liveWorkflowStatus = typeof workflowEvent?.status === 'string'
    ? workflowEvent.status
    : statusFromObject(liveWorkflowState);
  const resultWorkflowStatus = typeof workflowEvent?.result === 'string'
    ? workflowEvent.result
    : statusFromObject(resultWorkflowState);
  const effectiveWorkflowStatus = workflowEvent?.completed
    ? (resultWorkflowStatus || liveWorkflowStatus || 'completed')
    : (liveWorkflowStatus || resultWorkflowStatus || null);
  const effectiveWorkflowState = workflowEvent?.completed
    ? (resultWorkflowState || liveWorkflowState)
    : (liveWorkflowState || resultWorkflowState);
  const effectiveWorkflowStage = effectiveWorkflowState?.stage ?? null;
  const liveWorkflowError = workflowEvent?.error
    || effectiveWorkflowState?.error
    || null;

  const approvalRequestId = effectiveWorkflowState?.approval_request_id ?? null;
  const approvalRequestQuery = useApprovalRequestDetails(approvalRequestId);
  const approvalRequest = approvalRequestQuery.data ?? null;

  const statusCopy = buildStatusCopy({
    approvalRequest,
    workflowState: effectiveWorkflowState,
    effectiveStatus: effectiveWorkflowStatus,
    effectiveStage: effectiveWorkflowStage,
  });

  const terminalOutcome = approvalRequest?.status === 'rejected'
    ? 'error'
    : hasSpawnedApPosting(effectiveWorkflowState, effectiveWorkflowStage)
      ? 'ap_posting_started'
      : workflowEvent?.completed
        ? (isFailureStatus(resultWorkflowStatus || effectiveWorkflowStatus || liveWorkflowError) ? 'error' : 'completed')
        : (isFailureStatus(effectiveWorkflowStatus || liveWorkflowError) ? 'error' : null);

  useEffect(() => {
    if (terminalHandled || !terminalOutcome) {
      return;
    }

    setTerminalHandled(true);
    queryClient.invalidateQueries({ queryKey: ['documents'] });
    queryClient.invalidateQueries({ queryKey: ['stats'] });

    if (terminalOutcome === 'ap_posting_started') {
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      addNotification({
        message: `${workflow.filename} completed approval and started AP posting.`,
        type: 'success',
      });
      window.setTimeout(() => removeWorkflow(workflow.workflowId), 3500);
      return;
    }

    if (terminalOutcome === 'completed') {
      addNotification({
        message: `${workflow.filename} is ready for review.`,
        type: 'success',
      });
      window.setTimeout(() => removeWorkflow(workflow.workflowId), 3500);
      return;
    }

    addNotification({
      message:
        approvalRequest?.status === 'rejected'
          ? `Approval rejected for ${workflow.filename}.`
          : liveWorkflowError || `${workflow.filename} failed to process.`,
      type: 'error',
    });
    window.setTimeout(() => removeWorkflow(workflow.workflowId), 5000);
  }, [
    addNotification,
    approvalRequest?.status,
    liveWorkflowError,
    queryClient,
    removeWorkflow,
    terminalHandled,
    terminalOutcome,
    workflow.filename,
    workflow.workflowId,
  ]);

  const popupToneClass = terminalOutcome === 'completed' || terminalOutcome === 'ap_posting_started'
    ? 'border-green-200 bg-green-50 text-green-900'
    : terminalOutcome === 'error'
      ? 'border-red-200 bg-red-50 text-red-900'
      : 'border-blue-200 bg-blue-50 text-blue-900';

  return (
    <div
      className={clsx(
        'pointer-events-auto rounded-xl border shadow-lg px-4 py-3 backdrop-blur-sm',
        popupToneClass
      )}
    >
      <div className="flex items-start gap-3">
        {terminalOutcome === 'completed' || terminalOutcome === 'ap_posting_started' ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : terminalOutcome === 'error' ? (
          <XCircle className="h-5 w-5 text-red-600" />
        ) : (
          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">
              Processing {formatDocumentType(workflow.documentType)}: {workflow.filename}
            </p>
            <button
              type="button"
              onClick={() => removeWorkflow(workflow.workflowId)}
              className="rounded p-1 hover:bg-black/5 transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1 text-sm">{statusCopy.primary}</p>
          {statusCopy.secondary && (
            <p className="mt-1 text-xs opacity-90">{statusCopy.secondary}</p>
          )}
          {liveWorkflowError && (
            <p className="mt-1 text-xs opacity-90">{liveWorkflowError}</p>
          )}
          {!liveWorkflowError && error && (
            <p className="mt-1 text-xs opacity-90">{error}</p>
          )}
          <p className="mt-2 text-[11px] uppercase tracking-wide opacity-70">
            {isConnected ? 'Live updates' : isConnecting ? 'Connecting' : 'Reconnecting'}
          </p>
        </div>
      </div>
    </div>
  );
};

export const DocumentWorkflowNotifications = () => {
  const workflows = useDocumentWorkflowStore((state) => state.workflows);

  if (workflows.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[94] flex w-full max-w-md flex-col gap-3">
      {workflows.map((workflow) => (
        <DocumentWorkflowNotification key={workflow.workflowId} workflow={workflow} />
      ))}
    </div>
  );
};
